import React, { useEffect, useState, useCallback } from 'react';
import connectUser from '../connectUser';
import Icon from '../ui/Icon';
import HiveButton from '../ui/HiveButton';
import spaceApi, { notifySpacesChanged } from '../spaceApi';
import { getSpaceId, setActiveSpace, clearActiveSpace } from '../getSpaceId';
import './screens.css';

function SpacesScreen({ userInfo }) {
  const active = getSpaceId(userInfo);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [invite, setInvite] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [membersBySpace, setMembersBySpace] = useState({});
  const [renaming, setRenaming] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [invites, setInvites] = useState([]);
  const [removingMember, setRemovingMember] = useState(null);

  const doDelete = (space) => {
    spaceApi
      .deleteSpace(space.spaceId)
      .then((res) => {
        if (res?.data?.success === false) return;
        setDeleting(null);
        if (getSpaceId(userInfo) === space.spaceId) {
          clearActiveSpace(); // we were in it — drop back to home and reload
          window.location.reload();
        } else {
          load();
          notifySpacesChanged();
        }
      })
      .catch(() => {});
  };

  const refetchMembers = (spaceId) => {
    spaceApi
      .members(spaceId)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        setMembersBySpace((m) => ({ ...m, [spaceId]: list }));
      })
      .catch(() => {});
  };

  const removeMember = (spaceId, mem) => {
    spaceApi
      .removeMember(spaceId, mem)
      .then((res) => {
        if (res?.data?.success === false) return;
        refetchMembers(spaceId);
        load();
      })
      .catch(() => {});
  };

  const toggleMembers = (spaceId) => {
    if (expanded === spaceId) {
      setExpanded(null);
      return;
    }
    setExpanded(spaceId);
    if (!membersBySpace[spaceId]) {
      spaceApi
        .members(spaceId)
        .then((res) => {
          const list = Array.isArray(res?.data) ? res.data : [];
          setMembersBySpace((m) => ({ ...m, [spaceId]: list }));
        })
        .catch(() => setMembersBySpace((m) => ({ ...m, [spaceId]: [] })));
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    spaceApi
      .list()
      .then((res) => setSpaces(Array.isArray(res?.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  const loadInvites = useCallback(() => {
    spaceApi
      .pendingInvites()
      .then((res) => setInvites(Array.isArray(res?.data) ? res.data : []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    load();
    loadInvites();
  }, [load, loadInvites]);

  const acceptInvite = (spaceId) => {
    spaceApi
      .acceptInvite(spaceId)
      .then((res) => {
        if (res?.data?.success === false) return;
        loadInvites();
        load(); // the workspace you just joined now shows up in the list
      })
      .catch(() => {});
  };

  const declineInvite = (spaceId) => {
    spaceApi
      .declineInvite(spaceId)
      .then((res) => {
        if (res?.data?.success === false) return;
        loadInvites();
      })
      .catch(() => {});
  };

  const switchTo = (sid) => {
    if (sid === active) return;
    setActiveSpace(sid);
    window.location.reload(); // let every app pick up the new active space
  };

  const create = () => {
    const n = newName.trim();
    if (!n) return;
    spaceApi
      .create(n)
      .then(() => {
        setNewName('');
        setCreating(false);
        load();
        notifySpacesChanged();
      })
      .catch(() => {});
  };

  return (
    <div className="hive-container hive-screen">
      <h1 className="hive-h2 hive-screen-h">Workspaces</h1>
      <p className="hive-subtle">
        Your data lives in a workspace. Switch to a shared workspace to see and edit its data with the
        people you’ve invited.
      </p>

      {invites.length > 0 && (
        <div className="hive-invite-list">
          <h2 className="hive-invite-h">Pending invitations</h2>
          {invites.map((inv) => (
            <div key={inv.spaceId} className="hive-invite-card">
              <span className="hive-space-ic">
                <Icon name="Mail" size={18} color="#fff" />
              </span>
              <div className="hive-invite-body">
                <span className="hive-card-title">{inv.spaceName}</span>
                <span className="hive-meta">
                  {inv.invitedBy ? `Invited by ${inv.invitedBy}` : 'You were invited'}
                  {inv.role ? ` · ${inv.role}` : ''}
                </span>
              </div>
              <div className="hive-invite-actions">
                <button
                  type="button"
                  className="hive-invite-accept hive-tap"
                  onClick={() => acceptInvite(inv.spaceId)}
                >
                  Accept
                </button>
                <button
                  type="button"
                  className="hive-invite-decline hive-tap"
                  onClick={() => declineInvite(inv.spaceId)}
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="hive-empty"><p>Loading…</p></div>
      ) : (
        <>
          <div className="hive-card-list">
            {spaces.map((sp) => {
              const isActive = sp.spaceId === active;
              const isExpanded = expanded === sp.spaceId;
              const members = membersBySpace[sp.spaceId];
              return (
                <div key={sp.spaceId} className="hive-space-wrap">
                  <div
                    className={`hive-space-card hive-tap${isActive ? ' is-active' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => switchTo(sp.spaceId)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && switchTo(sp.spaceId)}
                  >
                    <span className="hive-space-ic">
                      <Icon name={sp.isHome ? 'Home' : 'Users'} size={20} color="#fff" />
                    </span>
                    <div className="hive-space-main">
                    <span className="hive-space-body">
                      <span className="hive-card-title">
                        {sp.name}
                        {isActive && <span className="hive-space-active"> · Active</span>}
                      </span>
                      <span className="hive-meta">
                        {sp.memberCount} member{sp.memberCount === 1 ? '' : 's'} · {sp.role}
                      </span>
                    </span>
                    <div className="hive-space-actions">
                      <button
                        type="button"
                        className="hive-space-mini"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMembers(sp.spaceId);
                        }}
                      >
                        <Icon name={isExpanded ? 'ChevronUp' : 'Users'} size={13} /> Members
                      </button>
                      {sp.role === 'owner' && (
                        <button
                          type="button"
                          className="hive-space-mini"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRenaming(sp);
                          }}
                        >
                          <Icon name="Pencil" size={13} /> Rename
                        </button>
                      )}
                      {sp.role === 'owner' && (
                        <button
                          type="button"
                          className="hive-space-invite"
                          onClick={(e) => {
                            e.stopPropagation();
                            setInvite(sp);
                          }}
                        >
                          Invite
                        </button>
                      )}
                    </div>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="hive-space-members">
                      {members == null ? (
                        <div className="hive-meta hive-space-members-msg">Loading…</div>
                      ) : members.length === 0 ? (
                        <div className="hive-meta hive-space-members-msg">Just you so far.</div>
                      ) : (
                        members.map((mem, i) => (
                          <div className="hive-space-member" key={mem.userId || mem.email || i}>
                            <span className="hive-space-member-avatar">
                              {(mem.name || mem.email || '?').charAt(0).toUpperCase()}
                            </span>
                            <span className="hive-space-member-body">
                              <span className="hive-space-member-name">{mem.name || mem.email}</span>
                              {mem.email && mem.name && <span className="hive-meta">{mem.email}</span>}
                            </span>
                            {mem.status === 'invited' ? (
                              <span className="hive-space-member-role hive-space-member-pending">
                                Invitation pending
                              </span>
                            ) : (
                              <span className="hive-space-member-role">
                                {mem.role === 'owner' ? 'Owner' : 'Member'}
                              </span>
                            )}
                            {sp.role === 'owner' && mem.role !== 'owner' && (
                              <button
                                type="button"
                                className="hive-space-member-remove"
                                onClick={() => setRemovingMember({ space: sp, mem })}
                                aria-label={mem.status === 'invited' ? 'Cancel invitation' : 'Remove member'}
                              >
                                <Icon name="X" size={14} />
                              </button>
                            )}
                          </div>
                        ))
                      )}
                      {sp.role === 'owner' && !sp.isHome && (
                        <button
                          type="button"
                          className="hive-space-delete"
                          onClick={() => setDeleting(sp)}
                        >
                          <Icon name="Trash2" size={14} /> Delete workspace
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {creating ? (
            <div className="hive-space-create">
              <input
                className="hive-field-input"
                placeholder="Workspace name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
              />
              <HiveButton variant="gold" onClick={create}>Create</HiveButton>
              <HiveButton variant="ghost" onClick={() => { setCreating(false); setNewName(''); }}>Cancel</HiveButton>
            </div>
          ) : (
            <button type="button" className="hive-space-new hive-tap" onClick={() => setCreating(true)}>
              <Icon name="Plus" size={16} /> New workspace
            </button>
          )}
        </>
      )}

      {invite && (
        <InviteModal
          space={invite}
          onClose={() => setInvite(null)}
          onDone={() => {
            const sid = invite.spaceId;
            setInvite(null);
            load();
            setExpanded(sid); // open the members list so the pending invite is visible
            refetchMembers(sid);
          }}
        />
      )}
      {renaming && (
        <RenameModal
          space={renaming}
          onClose={() => setRenaming(null)}
          onDone={() => {
            setRenaming(null);
            load();
            notifySpacesChanged(); // update the top-left workspace pill live
          }}
        />
      )}
      {deleting && (
        <DeleteConfirmModal
          space={deleting}
          onClose={() => setDeleting(null)}
          onConfirm={() => doDelete(deleting)}
        />
      )}
      {removingMember && (
        <RemoveMemberModal
          space={removingMember.space}
          mem={removingMember.mem}
          onClose={() => setRemovingMember(null)}
          onConfirm={() => {
            removeMember(removingMember.space.spaceId, removingMember.mem);
            setRemovingMember(null);
          }}
        />
      )}
    </div>
  );
}

function DeleteConfirmModal({ space, onClose, onConfirm }) {
  const [saving, setSaving] = useState(false);
  return (
    <div className="hive-modal-backdrop" onMouseDown={onClose}>
      <div className="hive-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="hive-modal-head">
          <h3 className="hive-modal-title">Delete “{space.name}”?</h3>
          <button type="button" className="hive-modal-close hive-tap" onClick={onClose} aria-label="Close">
            <Icon name="X" size={18} />
          </button>
        </div>
        <p className="hive-modal-sub">
          This removes the workspace and everyone’s access to it. Data created in it will no longer be
          reachable. This can’t be undone.
        </p>
        <div className="hive-modal-actions">
          <HiveButton variant="ghost" onClick={onClose}>Cancel</HiveButton>
          <button
            type="button"
            className="hive-danger-btn hive-tap"
            disabled={saving}
            onClick={() => {
              setSaving(true);
              onConfirm();
            }}
          >
            {saving ? 'Deleting…' : 'Delete workspace'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RemoveMemberModal({ space, mem, onClose, onConfirm }) {
  const [saving, setSaving] = useState(false);
  const isInvite = mem.status === 'invited';
  const who = mem.name || mem.email || 'this member';
  return (
    <div className="hive-modal-backdrop" onMouseDown={onClose}>
      <div className="hive-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="hive-modal-head">
          <h3 className="hive-modal-title">
            {isInvite ? 'Cancel invitation?' : 'Remove member?'}
          </h3>
          <button type="button" className="hive-modal-close hive-tap" onClick={onClose} aria-label="Close">
            <Icon name="X" size={18} />
          </button>
        </div>
        <p className="hive-modal-sub">
          {isInvite ? (
            <>Cancel the invitation to <strong>{who}</strong>? They won’t be able to join “{space.name}”.</>
          ) : (
            <>Remove <strong>{who}</strong> from “{space.name}”? They’ll immediately lose access to its data.</>
          )}
        </p>
        <div className="hive-modal-actions">
          <HiveButton variant="ghost" onClick={onClose}>Keep</HiveButton>
          <button
            type="button"
            className="hive-danger-btn hive-tap"
            disabled={saving}
            onClick={() => {
              setSaving(true);
              onConfirm();
            }}
          >
            {saving ? 'Removing…' : isInvite ? 'Cancel invitation' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RenameModal({ space, onClose, onDone }) {
  const [name, setName] = useState(space.name || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = () => {
    const n = name.trim();
    if (!n) return setErr('Name is required.');
    setErr(null);
    setSaving(true);
    spaceApi
      .rename(space.spaceId, n)
      .then((res) => {
        if (res?.data?.success === false) {
          setErr(res.data.message || 'Could not rename.');
          setSaving(false);
          return;
        }
        onDone();
      })
      .catch(() => {
        setErr('Could not rename.');
        setSaving(false);
      });
  };

  return (
    <div className="hive-modal-backdrop" onMouseDown={onClose}>
      <div className="hive-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="hive-modal-head">
          <h3 className="hive-modal-title">Rename workspace</h3>
          <button type="button" className="hive-modal-close hive-tap" onClick={onClose} aria-label="Close">
            <Icon name="X" size={18} />
          </button>
        </div>
        <label className="hive-field">
          <span className="hive-field-label">Name</span>
          <input className="hive-field-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        {err && <div className="hive-modal-err">{err}</div>}
        <div className="hive-modal-actions">
          <HiveButton variant="ghost" onClick={onClose}>Cancel</HiveButton>
          <HiveButton variant="gold" disabled={saving} onClick={submit}>{saving ? 'Saving…' : 'Save'}</HiveButton>
        </div>
      </div>
    </div>
  );
}

function InviteModal({ space, onClose, onDone }) {
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = () => {
    if (!email.trim()) return setErr('Email is required.');
    setErr(null);
    setSaving(true);
    spaceApi
      .invite(space.spaceId, email.trim())
      .then((res) => {
        if (res?.data?.success === false) {
          setErr(res.data.message || 'Could not invite.');
          setSaving(false);
          return;
        }
        onDone();
      })
      .catch(() => {
        setErr('Could not invite.');
        setSaving(false);
      });
  };

  return (
    <div className="hive-modal-backdrop" onMouseDown={onClose}>
      <div className="hive-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="hive-modal-head">
          <h3 className="hive-modal-title">Invite to {space.name}</h3>
          <button type="button" className="hive-modal-close hive-tap" onClick={onClose} aria-label="Close">
            <Icon name="X" size={18} />
          </button>
        </div>
        <p className="hive-modal-sub">
          They’ll get an invitation and must accept it before they can see this workspace’s data.
        </p>
        <label className="hive-field">
          <span className="hive-field-label">Email</span>
          <input className="hive-field-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" />
        </label>
        {err && <div className="hive-modal-err">{err}</div>}
        <div className="hive-modal-actions">
          <HiveButton variant="ghost" onClick={onClose}>Cancel</HiveButton>
          <HiveButton variant="gold" disabled={saving} onClick={submit}>{saving ? 'Inviting…' : 'Send invite'}</HiveButton>
        </div>
      </div>
    </div>
  );
}

export default connectUser(SpacesScreen);
